import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, SlidersHorizontal, X } from 'lucide-react'
import type { ProductCard } from '../../types'
import ProductGrid from '../../components/product/ProductGrid'
import ProductFilters from '../../components/product/ProductFilters'
import ProductSearch from '../../components/product/ProductSearch'
import ProductSort from '../../components/product/ProductSort'
import CategoryPagination from '../category/CategoryPagination'
import {
  DEFAULT_FILTER_SPEC,
  type FacetResponse,
} from '../../components/product/filters/types'
import {
  useVikeUrlAdapter,
  readAttributesFromParams,
} from '../../components/product/filters/urlAdapter'
import { fetchProducts } from '../../api/products'
import { fetchProductFacets } from '../../api/facets'

export interface SsrProductListViewProps {
  lang: string | undefined
  products: ProductCard[]
  productTotal: number
  /** Server-rendered facet counts. Seeds the TanStack cache. Null when
   *  the upstream call failed — the sidebar degrades gracefully. */
  facets: FacetResponse | null
  /** Initial querystring as seen by the SSR render. Passed through so
   *  the hydration adapter starts from the same state. */
  initialQueryString?: string
}

/**
 * SSR products listing view (WEB-85). Renders the full product catalogue
 * with search, sort, facet sidebar and pagination. Shares the same
 * hydration pattern as CategoryDetailView — server-rendered data seeds
 * the TanStack Query cache and is swapped out once the user interacts.
 */
export default function SsrProductListView({
  lang,
  products,
  productTotal,
  facets,
  initialQueryString = '',
}: SsrProductListViewProps) {
  const adapter = useVikeUrlAdapter(initialQueryString)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // ── Derive the live criteria from the adapter ──────────────────────────────
  const params = adapter.searchParams
  const category = params.get('category') ?? undefined
  const subcategory = params.get('subcategory') ?? undefined
  const search = params.get('search') ?? undefined
  const sort = params.get('sort') ?? undefined
  const brand = params.get('brand') ?? undefined
  const series = params.get('series') ?? undefined
  const priceRange = params.get('price_range') ?? undefined
  const stockStatus = params.get('stock_status') ?? undefined
  const deliverySpeed = params.get('delivery_speed') ?? undefined
  const labels = params.getAll('labels')
  const lifecycle = params
    .getAll('lifecycle_phase')
    .flatMap((v) => v.split(',').filter(Boolean))
  const legacyParam = params.get('legacy')
  const legacy = legacyParam === 'hide' || legacyParam === 'only' ? legacyParam : undefined
  const attributes = readAttributesFromParams(params)
  const hasAttributes = Object.keys(attributes).length > 0
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1)

  // Only reuse SSR snapshots as `initialData` when the live URL state
  // matches what SSR was run against — otherwise the client has navigated.
  const initialMatches = params.toString() === initialQueryString

  const productsQueryArgs = {
    category,
    subcategory,
    search,
    sort,
    brand,
    series,
    price_range: priceRange,
    stock_status: stockStatus,
    delivery_speed: deliverySpeed,
    labels: labels.length ? labels : undefined,
    lifecycle_phase: lifecycle.length ? lifecycle.join(',') : undefined,
    legacy,
    attributes: hasAttributes ? attributes : undefined,
    page,
    page_size: 24,
  }

  const productsQuery = useQuery({
    queryKey: [
      'products',
      'all',
      {
        category,
        subcategory,
        sort,
        search,
        brand,
        series,
        priceRange,
        stockStatus,
        deliverySpeed,
        labels,
        lifecycle,
        legacy,
        attributes,
        page,
      },
    ],
    queryFn: () => fetchProducts(productsQueryArgs),
    initialData: initialMatches
      ? {
          data: products,
          pagination: {
            page,
            page_size: 24,
            total: productTotal,
            total_pages: Math.max(1, Math.ceil(productTotal / 24)),
            has_next: page < Math.max(1, Math.ceil(productTotal / 24)),
            has_prev: page > 1,
          },
        }
      : undefined,
  })

  const facetsQuery = useQuery({
    queryKey: [
      'product-facets',
      'all',
      {
        category,
        subcategory,
        search,
        brand,
        series,
        priceRange,
        stockStatus,
        deliverySpeed,
        labels,
        lifecycle,
        legacy,
        attributes,
      },
    ],
    queryFn: () =>
      fetchProductFacets({
        category,
        subcategory,
        search,
        brand,
        series,
        price_range: priceRange,
        stock_status: stockStatus,
        delivery_speed: deliverySpeed,
        labels: labels.length ? labels : undefined,
        lifecycle_phase: lifecycle.length ? lifecycle.join(',') : undefined,
        legacy,
        attributes: hasAttributes ? attributes : undefined,
      }),
    initialData: initialMatches && facets ? facets : undefined,
  })

  const liveFacets = facetsQuery.data ?? (initialMatches ? facets : null) ?? undefined
  const liveProducts = productsQuery.data?.data ?? products
  const liveTotal = productsQuery.data?.pagination.total ?? productTotal

  // Active filter badge count for the mobile button.
  const activeFilterCount =
    (category ? 1 : 0) +
    (subcategory ? 1 : 0) +
    (search ? 1 : 0) +
    (sort ? 1 : 0) +
    (brand ? 1 : 0) +
    (series ? 1 : 0) +
    labels.length +
    lifecycle.length +
    (priceRange ? 1 : 0) +
    (stockStatus ? stockStatus.split(',').filter(Boolean).length : 0) +
    (deliverySpeed ? 1 : 0) +
    Object.values(attributes).reduce((sum, vs) => sum + vs.length, 0)

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {search && (
        <nav aria-label="Breadcrumb" className="text-sm text-neutral-600 mb-4">
          <ol className="flex flex-wrap items-center gap-1">
            <li>
              <a href={`/${encodeURIComponent(lang ?? 'nl')}`} className="hover:text-primary">Home</a>
            </li>
            <li aria-hidden="true"><ChevronRight className="h-3 w-3 inline-block" /></li>
            <li>
              <a href={`/${encodeURIComponent(lang ?? 'nl')}/products`} className="hover:text-primary">Producten</a>
            </li>
            <li aria-hidden="true"><ChevronRight className="h-3 w-3 inline-block" /></li>
            <li className="text-neutral-700 font-medium" aria-current="page">Zoekresultaten</li>
          </ol>
        </nav>
      )}
      <h1 className="text-2xl font-bold text-neutral-900 mb-1">
        {search && liveTotal === 0
          ? `Geen resultaten voor "${search}"`
          : search
          ? `Zoekresultaten voor "${search}"`
          : subcategory
          ? subcategory
          : category
          ? category
          : 'Alle producten'}
      </h1>
      <p className="text-sm text-neutral-600 mb-6">
        {liveTotal} {liveTotal === 1 ? 'product' : 'producten'}
      </p>

      {/* search + sort bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <ProductSearch adapter={adapter} />
        <div className="ml-auto">
          <ProductSort adapter={adapter} />
        </div>
        <button
          type="button"
          onClick={() => setMobileFiltersOpen(true)}
          className="lg:hidden inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-50"
          aria-label="Filters openen"
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-white text-xs font-semibold">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className="flex gap-8">
        <aside
          className="hidden lg:block w-64 shrink-0"
          data-testid="product-filter-sidebar"
        >
          <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1">
            <ProductFilters spec={DEFAULT_FILTER_SPEC} facets={liveFacets} adapter={adapter} />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <ProductGrid
            products={liveProducts}
            isLoading={productsQuery.isLoading}
            searchQuery={search}
            clearSearchHref={search ? `/${lang ?? 'nl'}/products` : undefined}
          />
          {productsQuery.data && (
            <CategoryPagination
              pagination={productsQuery.data.pagination}
              adapter={adapter}
            />
          )}
        </div>
      </div>

      {mobileFiltersOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-80 max-w-[90%] bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
              <h2 className="text-base font-semibold text-neutral-900">Filters</h2>
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(false)}
                className="p-1 rounded hover:bg-neutral-100"
                aria-label="Filters sluiten"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-testid="product-filter-drawer">
              <ProductFilters spec={DEFAULT_FILTER_SPEC} facets={liveFacets} adapter={adapter} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
